import { useState } from "react";
import { Alert, Button, InputText } from "@/deps/ui-library";
import { Dialog } from "@/components/Dialog";

export interface RenameField {
  key: string;
  label: string;
  value: string;
}

/**
 * Renames what a country pack or chart template suggested (ADR-0038). Only the name changes: codes,
 * rates and everything already posted stay as they are. Only the fields that changed are saved.
 */
export function RenameDialog({
  title,
  note,
  fields,
  onSave,
  onClose,
}: {
  title: string;
  note?: string;
  fields: RenameField[];
  onSave: (changed: Array<{ key: string; name: string }>) => Promise<unknown>;
  onClose: () => void;
}) {
  const [names, setNames] = useState<Record<string, string>>(
    Object.fromEntries(fields.map((field) => [field.key, field.value])),
  );
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const blank = fields.some((field) => (names[field.key] ?? "").trim() === "");

  const save = async () => {
    setSaving(true);
    setRefused(null);
    try {
      await onSave(
        fields
          .filter((field) => (names[field.key] ?? "").trim() !== field.value)
          .map((field) => ({ key: field.key, name: (names[field.key] ?? "").trim() })),
      );
      onClose();
    } catch (error) {
      setRefused(error instanceof Error ? error.message : "That could not be saved.");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={blank || saving}
          onClick={() => void save()}
        >
          {saving ? "Saving" : "Save"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {refused ? (
          <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
        ) : null}
        {fields.map((field) => (
          <InputText
            key={field.key}
            label={field.label}
            required
            maxLength={200}
            value={names[field.key] ?? ""}
            onChange={(event) => setNames({ ...names, [field.key]: event.target.value })}
          />
        ))}
        {note ? <p className="text-fg-muted text-xs">{note}</p> : null}
      </div>
    </Dialog>
  );
}
